// 분할 보기 — 단지별 패널로 상단 가격 차트(매매/전세)를 나눠서 렌더
import React, { useMemo, useRef, useEffect, useState, useCallback } from 'react';
import ChartCard from './ChartCard';
import { createTopOption } from './TopChart';
import {
  CHART_METRICS, SALE_MAIN_METRICS, LEASE_MAIN_METRICS,
  TOP_TOGGLE_KEYS, TOP_BRUSH, TOP_OPTION_PREFIX,
} from './constants';
import { darkenHex, getEndLabelStyle, formatUseApproveDate } from './utils';

// 합쳐진 상단 옵션을 단지별로 분리 (series 를 단지명으로 필터)
function createTopSplitOptions(baseOption, filteredItems) {
  if (filteredItems.length <= 1) return [];
  return filteredItems.map((item) => {
    const complexName = item.displayName || item.buildingName || item.naverDanjiCode;
    const addressText = String(item?.address || '').trim();
    const metaParts = [];
    if (item?.familyCnt != null) metaParts.push(`${Number(item.familyCnt).toLocaleString('ko-KR')}세대`);
    if (item?.useApproveDate) metaParts.push(`사용승인 ${formatUseApproveDate(item.useApproveDate)}`);
    const metaText = metaParts.join(' · ');

    const baseSeries = (Array.isArray(baseOption?.series) ? baseOption.series : [])
      .filter((entry) => String(entry?.name || '').startsWith(`${complexName} `));
    const series = baseSeries.map((entry) => {
      const seriesColor = entry?.lineStyle?.color || entry?.itemStyle?.color || '#334155';
      if (entry?.type === 'line') {
        const metric = CHART_METRICS.find((row) => String(entry?.name || '').endsWith(` ${row.label}`));
        // 실거래가 라인은 끝점 마커 라벨(최근 실거래가+날짜)로 표기하므로 기본 endLabel 생략
        const isRealPrice = metric?.key === 'saleRealPrice' || metric?.key === 'leaseRealPrice';
        return {
          ...entry,
          endLabel: isRealPrice ? undefined : (metric?.label ? getEndLabelStyle(metric.label, seriesColor) : entry?.endLabel),
          labelLayout: { moveOverlap: 'shiftY' },
        };
      }
      if (entry?.type === 'scatter' && String(entry?.name || '').endsWith(' 최고가')) {
        return {
          ...entry,
          label: { show: true, formatter: '최고가', position: 'top', color: darkenHex(seriesColor, 0.45), fontSize: 11, fontWeight: 800 },
        };
      }
      return entry;
    });

    return {
      code: item.compareKey ?? item.naverDanjiCode,
      complexName,
      addressText,
      metaText,
      option: {
        ...baseOption,
        // 제목(매매 가격/전세 가격)은 우상단에 유지
        title: baseOption?.title,
        dataZoom: (Array.isArray(baseOption?.dataZoom) ? baseOption.dataZoom : []).map((entry, index) => ({
          ...entry,
          id: `ranking-compare-split-${item.compareKey ?? item.naverDanjiCode}-${index}-dataZoomX`,
        })),
        // 단지별 보기는 항상 우측 endLabel/끝점 라벨이 있으므로 우측 여백을 넉넉히 확보(잘림 방지)
        grid: { ...(baseOption?.grid || {}), left: 64, right: 132, top: 44, bottom: 46 },
        series,
      },
    };
  });
}

export default function SplitTopChart({
  filteredItems,
  colorByCode,
  metricVisible,
  syncedZoom,
  brushRangeRows,
  showSaleTopChart,
  showLeaseTopChart,
  onToggle,
  onBrushClose,
  registerSplitRef, // (key, instance) => void
  chartEvents,
  height,
}) {
  const optionDeps = [filteredItems, colorByCode, metricVisible, syncedZoom];

  const saleSplit = useMemo(() => {
    const base = createTopOption({ filteredItems, colorByCode, metricVisible, syncedZoom, metricKeys: SALE_MAIN_METRICS, optionPrefix: TOP_OPTION_PREFIX.sale, chartTitle: '매매 가격' });
    return createTopSplitOptions(base, filteredItems);
  }, optionDeps); // eslint-disable-line react-hooks/exhaustive-deps

  const leaseSplitByCode = useMemo(() => {
    const base = createTopOption({ filteredItems, colorByCode, metricVisible, syncedZoom, metricKeys: LEASE_MAIN_METRICS, optionPrefix: TOP_OPTION_PREFIX.lease, chartTitle: '전세 가격' });
    const map = new Map();
    createTopSplitOptions(base, filteredItems).forEach((row) => map.set(row.code, row.option));
    return map;
  }, optionDeps); // eslint-disable-line react-hooks/exhaustive-deps

  const needScroll = saleSplit.length >= 3;
  const scrollRef = useRef(null);   // 실제 패널 가로 스크롤 컨테이너
  const stickyRef = useRef(null);   // 하단 고정 프록시 스크롤바
  const [contentWidth, setContentWidth] = useState(0);
  const syncingRef = useRef(false);

  // 콘텐츠 전체 너비 측정(패널/차트 렌더 후) — 프록시 바 폭에 반영
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || !needScroll) return undefined;
    const measure = () => setContentWidth(el.scrollWidth);
    measure();
    const t1 = setTimeout(measure, 120);
    const t2 = setTimeout(measure, 400);
    let ro;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(measure);
      ro.observe(el);
    }
    window.addEventListener('resize', measure);
    return () => {
      clearTimeout(t1); clearTimeout(t2);
      if (ro) ro.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [needScroll, saleSplit.length, showSaleTopChart, showLeaseTopChart, height]);

  // 메인 컨테이너 ↔ 하단 프록시 바 가로 스크롤 동기화
  const onMainScroll = useCallback(() => {
    if (syncingRef.current) return;
    syncingRef.current = true;
    if (stickyRef.current && scrollRef.current) stickyRef.current.scrollLeft = scrollRef.current.scrollLeft;
    syncingRef.current = false;
  }, []);
  const onStickyScroll = useCallback(() => {
    if (syncingRef.current) return;
    syncingRef.current = true;
    if (stickyRef.current && scrollRef.current) scrollRef.current.scrollLeft = stickyRef.current.scrollLeft;
    syncingRef.current = false;
  }, []);

  return (
    <>
    <div
      ref={scrollRef}
      onScroll={onMainScroll}
      className={`ranking-compare-top-chart-scroll${needScroll ? ' is-scroll hide-native-scrollbar' : ''}`}
    >
      {saleSplit.map((row) => (
        <div key={row.code} className="ranking-compare-top-chart-panel" style={{ position: 'relative' }}>
          <div className="ranking-compare-panel-header">
            <div className="ranking-compare-panel-title">
              <h4>{row.complexName}</h4>
              {row.addressText && <p className="address">{row.addressText}</p>}
              {row.metaText && <p className="meta">{row.metaText}</p>}
            </div>
          </div>

          {showSaleTopChart && (
            <ChartCard
              wrapperClassName={null}
              panelPrefix={`ranking-compare-split-sale-${row.code}`}
              title="매매 가격"
              brushTitle={TOP_BRUSH.sale.title}
              brushMetrics={TOP_BRUSH.sale.metrics}
              brushRangeRows={brushRangeRows}
              metricVisible={metricVisible}
              onBrushClose={onBrushClose}
              toggleKeys={TOP_TOGGLE_KEYS.sale}
              onToggle={onToggle}
              option={row.option}
              chartRef={(instance) => registerSplitRef(`sale-${row.code}`, instance)}
              height={height}
              chartEvents={chartEvents}
            />
          )}

          {showLeaseTopChart && (
            <ChartCard
              wrapperClassName={null}
              panelPrefix={`ranking-compare-split-lease-${row.code}`}
              title="전세 가격"
              brushTitle={TOP_BRUSH.lease.title}
              brushMetrics={TOP_BRUSH.lease.metrics}
              brushRangeRows={brushRangeRows}
              metricVisible={metricVisible}
              onBrushClose={onBrushClose}
              toggleKeys={TOP_TOGGLE_KEYS.lease}
              onToggle={onToggle}
              option={leaseSplitByCode.get(row.code)}
              chartRef={(instance) => registerSplitRef(`lease-${row.code}`, instance)}
              height={height}
              chartEvents={chartEvents}
            />
          )}
        </div>
      ))}
    </div>
    {needScroll && (
      <div
        ref={stickyRef}
        onScroll={onStickyScroll}
        className="ranking-compare-hscroll-sticky"
        aria-hidden="true"
      >
        <div style={{ width: contentWidth, height: 1 }} />
      </div>
    )}
    </>
  );
}
